// Read-Only Mode Banner Component
import React from 'react';
import { useOfflineMode } from '../../hooks/useOfflineMode.js';
import { EyeIcon, WifiIcon } from '@heroicons/react/24/outline';

/**
 * Banner that appears when the app is in read-only mode
 * Shows available cached data and offline limitations
 */
export function ReadOnlyModeBanner({ className = '' }) {
  const { readOnlyMode, capabilities } = useOfflineMode();

  if (!readOnlyMode) {
    return null;
  }

  const hasData = capabilities?.availableData?.length > 0;
  const availableData = capabilities?.availableData || [];

  return (
    <div className={`mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 ${className}`}>
      <div className="flex items-start space-x-2">
        <EyeIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium text-blue-800">Read-Only Mode</h4>
          <div className="mt-1 text-sm text-blue-700">
            {hasData ? (
              <>
                <p>You're viewing cached data. Changes cannot be made while offline.</p>
                {availableData.length > 0 && (
                  <div className="mt-2">
                    <span className="font-medium">Available data: </span>
                    <span className="text-blue-600">
                      {availableData
                        .map((data) => {
                          const labels = {
                            sessions: 'Training Sessions',
                            availability: 'Availability',
                            crews: 'Crews',
                            users: 'Users',
                            planning: 'Planning Data',
                          };
                          return labels[data] || data;
                        })
                        .join(', ')}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <p>No cached data available. Connect to internet to view latest information.</p>
            )}
          </div>
          <div className="mt-2 flex items-center text-xs text-blue-600">
            <WifiIcon className="mr-1 h-4 w-4" />
            <span>Connect to internet to enable all features</span>
          </div>
        </div>
      </div>
    </div>
  );
}
